
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import { useAnalytics } from "@/lib/analytics";
import { AnalyticsEvents } from "@/lib/analytics";

interface NavbarProps {
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
}

export default function Navbar({ onToggleSidebar, isSidebarOpen }: NavbarProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { toggleSidebar, openMobile, setOpenMobile } = useSidebar();
  const { trackEvent, trackButtonClick } = useAnalytics();
  
  // Show profile/logout only on homepage where sidebar is not available
  const isHomepage = location.pathname === '/';
  const showUserControls = user && isHomepage;

  const handleMenuClick = () => {
    trackButtonClick('toggle_sidebar');
    
    if (toggleSidebar) {
      toggleSidebar();
    } else if (onToggleSidebar) {
      onToggleSidebar();
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <nav className="glass fixed top-0 left-0 right-0 z-50 h-16 px-4 md:px-6 flex items-center justify-between shadow-sm">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={handleMenuClick}
          aria-label={openMobile ? "Close sidebar" : "Open sidebar"}
        >
          {openMobile ? <X size={20} /> : <Menu size={20} />}
        </Button>
        <Link to="/" className="flex items-center space-x-2">
          <img src="/mishu_svg.svg" alt="mishu" className="h-8 w-auto" />
        </Link>
      </div>
      
      <div className="flex items-center space-x-3">
        {showUserControls && (
          <>
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                {user.displayName ? user.displayName[0].toUpperCase() : user.email?.[0].toUpperCase()}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium">
                  {user.displayName || user.email}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              aria-label="Logout"
            >
              <LogOut size={18} />
            </Button>
          </>
        )}
      </div>
    </nav>
  );
}
